import { useCallback, useEffect, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import {
  getActivities,
  getLogs,
  type ActivityEntry,
  type LogEntry,
} from "../../../lib/dashboardAdmin";
import { ApiProblemError } from "../../../lib/authManager";
import { useServerActivity } from "../../../lib/activityHub";
import { useServers } from "../../../lib/servers";
import { useServerEvents } from "../../../lib/useServerEvents";
import { Chip, ErrorNote, SegmentedTabs, Select, Spinner } from "../../../components/ui";
import {
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetMessage,
  SheetNotice,
  SheetRows,
  SheetSection,
} from "../../../components/settings-sheet";
import { ServerSettingsGuard } from "../../../components/server-settings";
import { InProgressSection } from "../../../components/dashboard/in-progress";
import {
  ANALYSIS_VERB_PREFIXES,
  alertsFeedChanged,
  formatActivityEvent,
  parseEventAt,
} from "../../../lib/activityFeed";
import type { ActivityEventData } from "../../../lib/eventsSocket";
import { useNavServers } from "../../../lib/navServers";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  MegaphoneIcon,
  TagIcon,
  TrashIcon,
  WarningTriangleIcon,
  XCircleIcon,
} from "../../../components/icons";

/**
 * Alerts + Console on one page (Plex splits them; both are "what is the
 * server doing", so they share a screen here). Alerts is the persisted
 * activity feed — unlike Plex's per-tab alerts it survives reloads.
 * Console is the live slog tail: ring-buffer backlog over REST, then
 * `log` events on the WebSocket (owner-only, opt-in via filters).
 */

type Tab = "alerts" | "console";

export const Route = createFileRoute("/_authed/settings/server/$serverId/activity")({
  validateSearch: (search: Record<string, unknown>): { tab: Tab } => ({
    tab: search.tab === "console" ? "console" : "alerts",
  }),
  component: ServerActivity,
});

const PAGE_SIZE = 50;
const CONSOLE_CAP = 2000;

function is404(err: unknown): boolean {
  return err instanceof ApiProblemError && err.problem.status === 404;
}

function fmtTime(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour12: false });
}

function fmtDateTime(at: number): string {
  const d = new Date(at);
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${fmtTime(at)}`;
}

function ServerActivity() {
  const { serverId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // Subscribe to the registry: getSource() is a sync snapshot, and the
  // queries would stay disabled forever without the hydration rerender.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;

  return (
    <ServerSettingsGuard serverId={serverId} heading="Activity">
      {() => (
        <div>
          <SheetHeader
            title="Activity"
            action={
              <SegmentedTabs
                value={tab}
                label="Activity view"
                options={[
                  { value: "alerts", label: "Alerts" },
                  { value: "console", label: "Console" },
                ]}
                onChange={(t) => void navigate({ search: { tab: t }, replace: true })}
              />
            }
          />

          {!isServer ? (
            <SheetLoading label="Connecting…" />
          ) : tab === "alerts" ? (
            <AlertsTab serverId={serverId} source={source as ServerSource} />
          ) : (
            <ConsoleTab serverId={serverId} source={source as ServerSource} />
          )}
        </div>
      )}
    </ServerSettingsGuard>
  );
}

// ---- alerts ----

const MAX_LIVE = 100; // synthetic rows kept between reconciles
const FLUSH_MS = 100; // WS batch window — ≤10 renders/s during floods

function AlertsTab({ serverId, source }: { serverId: string; source: ServerSource }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const { sources } = useSources();
  const nav = useNavServers(sources);
  // Library titles for the Running now rows (queued scans carry none)
  // and the live rows' library names — one lookup, stable while the nav
  // data is, like the dashboard's.
  const navData = nav.data;
  const libraryTitle = useCallback(
    (libraryId: string) =>
      navData?.find((s) => s.id === serverId)?.libraries.find((l) => l.id === libraryId)?.title,
    [navData, serverId],
  );
  // Broker-offline: the guard swaps the page for the unavailable screen —
  // no lease and no tasks poll behind it (the dashboard's gating).
  const { servers } = useServers();
  const online = servers.find((s) => s.id === serverId)?.online ?? false;
  const { connected } = useServerActivity(serverId);

  const activities = useQuery({
    queryKey: ["dashboard", "activities", serverId, page],
    // The socket drives the refetches; a slow poll only while it is down
    // (PNA on the direct path), as the troubleshooting page does.
    refetchInterval: connected ? false : 15_000,
    queryFn: () => getActivities(source.conn, page * PAGE_SIZE, PAGE_SIZE),
  });
  const notSupported = is404(activities.error);

  // Live rows synthesized straight off the socket (Plex-style): they
  // render the instant an event lands and are superseded by the REST
  // feed on the next throttled refetch. The recorder keeps each event's
  // own timestamp, so a persisted twin shares the live row's millisecond:
  // a live row is done once it is older than the newest persisted row,
  // or a persisted row of the same stamp, kind, title and item exists
  // (see superseded) — never a duplicate, never a lost row.
  const [liveRows, setLiveRows] = useState<ActivityEntry[]>([]);
  const pending = useRef<ActivityEntry[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveSeq = useRef(0);
  const restRows = useRef<ActivityEntry[]>([]);
  restRows.current = activities.data?.items ?? [];

  // REST reconciliation driver: refresh page 0 at most once per second —
  // a scan floods hundreds of per-item events — and once on reconnect.
  const refresh = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armRefetch = useCallback(() => {
    if (refresh.current) return;
    refresh.current = setTimeout(() => {
      refresh.current = null;
      void qc.invalidateQueries({ queryKey: ["dashboard", "activities", serverId, 0] });
    }, 1000);
  }, [qc, serverId]);

  useServerEvents(serverId, (ev) => {
    // Reconnect snapshot replays rebuild hub state; they are not news —
    // synthesizing rows or refetching from them would dupe every reconnect.
    // Status ticks change no row either. A playback error persists as a
    // session.error row with no live synthesis (rare, not a cascade) —
    // it just pulls the feed.
    if (!alertsFeedChanged(ev)) return;
    armRefetch();
    if (ev.type !== "activity" || !ev.data) return;
    if (page !== 0 || notSupported) return;
    const entry = formatActivityEvent(
      ev.data as ActivityEventData,
      parseEventAt(ev.at),
      `live-${liveSeq.current++}`,
      libraryTitle,
    );
    if (!entry) return;
    pending.current.push(entry);
    if (flushTimer.current) return;
    // Each WS message is its own macrotask, so without this buffer a
    // flood costs one render per event.
    flushTimer.current = setTimeout(() => {
      flushTimer.current = null;
      const fresh = pending.current;
      pending.current = [];
      fresh.reverse(); // arrival order → newest first
      setLiveRows((prev) =>
        [...fresh, ...prev].filter((r) => !superseded(r, restRows.current)).slice(0, MAX_LIVE),
      );
    }, FLUSH_MS);
  });

  useEffect(
    () => () => {
      if (refresh.current) clearTimeout(refresh.current);
      if (flushTimer.current) clearTimeout(flushTimer.current);
    },
    [],
  );

  // The socket came back: whatever the outage recorded sits on page 1
  // now, and the replay that rebuilt the hub is deliberately not news
  // above. A page that mounted while the socket was down refetches once
  // it opens — events between its REST fetch and the open were never
  // delivered. The first report is the baseline, not a transition.
  const prevConnected = useRef<boolean | null>(null);
  useEffect(() => {
    const prev = prevConnected.current;
    prevConnected.current = connected;
    if (prev === false && connected) armRefetch();
  }, [connected, armRefetch]);

  // Paging leaves page 0: drop the live layer so returning can't dupe.
  useEffect(() => {
    pending.current = [];
    setLiveRows([]);
  }, [page]);

  // Hygiene trim when REST truth lands (display correctness is the
  // render-time filter below — this just bounds the state).
  useEffect(() => {
    const rest = activities.data?.items ?? [];
    setLiveRows((prev) =>
      prev.some((r) => superseded(r, rest)) ? prev.filter((r) => !superseded(r, rest)) : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities.dataUpdatedAt]);

  if (notSupported) {
    return (
      <SheetNotice title="Alerts">
        This server doesn't have the alerts feed yet — update rago-server.
      </SheetNotice>
    );
  }

  const total = activities.data?.meta.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items = activities.data?.items ?? [];
  const visibleLive = page === 0 ? liveRows.filter((r) => !superseded(r, items)) : [];

  return (
    <>
      {/* What the server is doing right now, above the feed: a stage that
          writes few or no rows (older servers persist nothing while they
          analyze) still reads as live progress, not a dead list — the
          dashboard's In progress rows, hidden when nothing runs. */}
      <InProgressSection
        serverId={serverId}
        source={online ? source : null}
        libraryTitle={libraryTitle}
        title="Running now"
        hideWhenIdle
      />
      {activities.error ? (
        <SheetInset>
          <ErrorNote error={activities.error} />
        </SheetInset>
      ) : null}
      <SheetSection
        title="Alerts"
        flush
        action={
          pages > 1 ? (
            <span className="flex items-center gap-2 text-xs text-[var(--rago-text-3)]">
              Page {page + 1} of {pages}
              <button
                className="cursor-pointer rounded px-1.5 py-0.5 text-[var(--rago-text-2)] transition-colors hover:bg-[var(--rago-bg-3)] disabled:cursor-default disabled:opacity-40"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <ChevronLeftIcon size={14} />
              </button>
              <button
                className="cursor-pointer rounded px-1.5 py-0.5 text-[var(--rago-text-2)] transition-colors hover:bg-[var(--rago-bg-3)] disabled:cursor-default disabled:opacity-40"
                disabled={page >= pages - 1}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRightIcon size={14} />
              </button>
            </span>
          ) : undefined
        }
      >
        {activities.isLoading ? (
          <SheetMessage loading>Loading…</SheetMessage>
        ) : visibleLive.length + items.length === 0 ? (
          <SheetMessage>
            Nothing yet — scans, media analysis and playback problems land here.
          </SheetMessage>
        ) : (
          <SheetRows>
            {visibleLive.map((a) => (
              <AlertRow key={a.id} entry={a} serverId={serverId} live />
            ))}
            {items.map((a) => (
              <AlertRow key={a.id} entry={a} serverId={serverId} />
            ))}
          </SheetRows>
        )}
      </SheetSection>
    </>
  );
}

/** A live row has been persisted (or is older than everything the feed
 * still shows) once the newest REST row is newer, or a REST row shares
 * its millisecond, kind, title and item — the server stamps rows with
 * the event's own time, so the twin never sorts after the live row. */
function superseded(r: ActivityEntry, rest: ActivityEntry[]): boolean {
  const newest = rest[0]?.at ?? 0;
  if (r.at < newest) return true;
  if (r.at > newest) return false;
  return rest.some(
    (p) =>
      p.at === r.at &&
      p.kind === r.kind &&
      p.title === r.title &&
      (p.itemId ?? "") === (r.itemId ?? ""),
  );
}

/** Per-kind glyph, Plex-style: megaphone = scanner touched a file, tag =
 * metadata stage, check = done. Unknown kinds keep the level dot. */
function kindIcon(kind: string) {
  if (kind === "scan.started" || kind === "scan.item" || kind === "scan.analyzed") return MegaphoneIcon;
  if (kind === "scan.finished") return CheckIcon;
  if (kind === "scan.failed" || kind === "scan.skipped" || kind === "session.error")
    return WarningTriangleIcon;
  if (kind === "scan.cancelled") return XCircleIcon;
  if (kind === "library.deleted") return TrashIcon;
  if (kind === "metadata.updated") return CheckIcon;
  if (kind === "metadata.failed") return WarningTriangleIcon;
  if (kind.startsWith("metadata.")) return TagIcon;
  if (kind === "task.started") return ClockIcon;
  if (kind === "task.finished") return CheckIcon;
  if (kind === "task.failed") return WarningTriangleIcon;
  if (kind === "task.cancelled") return XCircleIcon;
  if (kind === "analysis.finished") return CheckIcon;
  if (kind === "analysis.failed") return WarningTriangleIcon;
  return null;
}

// The server pre-renders row text; splitting a known verb prefix lets the
// item name carry the link color alone, like Plex's alert rows.
const VERB_PREFIXES = [
  "Scanning ",
  "Loading metadata for ",
  "Matched ",
  "Finished processing ",
  "Metadata for ",
  ...ANALYSIS_VERB_PREFIXES,
];

function splitVerb(title: string): [string, string] | null {
  for (const p of VERB_PREFIXES) {
    if (title.startsWith(p) && title.length > p.length) return [p, title.slice(p.length)];
  }
  return null;
}

function AlertRow({
  entry,
  serverId,
  live,
}: {
  entry: ActivityEntry;
  serverId: string;
  /** Socket-synthesized row — gets the entrance animation; its REST
   * replacement renders plain, so reconciliation is visually seamless. */
  live?: boolean;
}) {
  const color =
    entry.level === "error"
      ? "var(--rago-error)"
      : entry.level === "warn"
        ? "var(--rago-warn)"
        : "var(--rago-text-3)";
  const Glyph = kindIcon(entry.kind);
  const split = entry.itemId ? splitVerb(entry.title) : null;
  const linked = (text: string) => (
    <Link
      to="/item/$sourceId/$itemId"
      params={{ sourceId: serverId, itemId: entry.itemId! }}
      className="text-[var(--rago-accent)] no-underline hover:underline"
    >
      {text}
    </Link>
  );
  return (
    <li
      className={`flex items-baseline gap-3 px-[var(--rago-sheet-x)] py-2.5${live ? " rago-row-in" : ""}`}
    >
      {Glyph ? (
        <span
          aria-hidden
          className="shrink-0 translate-y-[2px] self-start"
          style={{ color }}
          title={entry.level}
        >
          <Glyph size={15} />
        </span>
      ) : (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full"
          style={{
            background: entry.level === "info" ? "var(--rago-ok)" : color,
          }}
          title={entry.level}
        />
      )}
      <span className="w-28 shrink-0 text-xs text-[var(--rago-text-3)]" title={String(entry.at)}>
        {fmtDateTime(entry.at)}
      </span>
      <span className="min-w-0 flex-1 text-sm text-[var(--rago-text-1)]">
        {split ? (
          <>
            {split[0]}
            {linked(split[1])}
          </>
        ) : entry.itemId ? (
          linked(entry.title)
        ) : (
          entry.title
        )}
        {entry.detail ? (
          <span className="ml-2 text-xs text-[var(--rago-text-3)]">{entry.detail}</span>
        ) : null}
      </span>
    </li>
  );
}

// ---- console ----

const LEVEL_RANK = { verbose: 0, debug: 1, info: 2, warn: 3, error: 4 } as const;
type MinLevel = keyof typeof LEVEL_RANK;

function ConsoleTab({ serverId, source }: { serverId: string; source: ServerSource }) {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [minLevel, setMinLevel] = useState<MinLevel>("verbose");
  const [paused, setPaused] = useState(false);
  const lastSeq = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const backlog = useQuery({
    queryKey: ["dashboard", "logs", serverId],
    staleTime: 0,
    queryFn: () => getLogs(source.conn, 500),
  });

  const append = (fresh: LogEntry[]) => {
    const next = fresh.filter((e) => e.seq > lastSeq.current);
    const newest = next[next.length - 1];
    if (!newest) return;
    lastSeq.current = newest.seq;
    setEntries((prev) => {
      const merged = [...prev, ...next];
      return merged.length > CONSOLE_CAP ? merged.slice(merged.length - CONSOLE_CAP) : merged;
    });
  };

  // Backlog first (seq cursors dedupe the seam with the live stream).
  useEffect(() => {
    if (backlog.data) append(backlog.data.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backlog.data]);

  const { connected } = useServerEvents(
    serverId,
    (ev) => {
      if (ev.type === "log" && ev.data) append([ev.data as unknown as LogEntry]);
    },
    ["log"],
  );

  const visible = entries.filter((e) => LEVEL_RANK[e.level] >= LEVEL_RANK[minLevel]);

  // Autoscroll unless paused (reading scrollback).
  useEffect(() => {
    if (paused) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [visible.length, paused]);

  if (is404(backlog.error)) {
    return (
      <SheetNotice title="Console">
        This server doesn't have the console yet — update rago-server.
      </SheetNotice>
    );
  }

  return (
    <>
      {backlog.error ? (
        <SheetInset>
          <ErrorNote error={backlog.error} />
        </SheetInset>
      ) : null}
      <SheetSection
        title="Console"
        flush
        action={
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-44">
              <Select
                id="console-level"
                value={minLevel}
                onChange={(v) => setMinLevel(v as MinLevel)}
              >
                <option value="verbose">All levels</option>
                <option value="info">Info and above</option>
                <option value="warn">Warnings and errors</option>
                <option value="error">Errors only</option>
              </Select>
            </div>
            <button
              type="button"
              className="rago-btn rago-btn-quiet rago-btn-sm"
              onClick={() => setPaused((p) => !p)}
            >
              {paused ? "Resume" : "Pause"}
            </button>
            <Chip tone={connected ? "ok" : "muted"}>{connected ? "Live" : "Reconnecting…"}</Chip>
          </div>
        }
      >
      {/* The well runs edge to edge under the section head; it scrolls on
          its own (autoscroll above) inside the sheet's scroller. */}
      <div
        ref={scrollRef}
        className="h-[min(60vh,640px)] overflow-y-auto border-y border-[var(--rago-line-1)] bg-[rgba(0,0,0,0.28)] px-[var(--rago-sheet-x)] py-3 font-mono text-[11px] leading-relaxed"
      >
        {backlog.isLoading && visible.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-[var(--rago-text-2)]">
            <Spinner /> Loading…
          </p>
        ) : visible.length === 0 ? (
          <p className="text-[var(--rago-text-3)]">No log lines yet.</p>
        ) : (
          visible.map((e) => <LogRow key={e.seq} entry={e} />)
        )}
      </div>
      </SheetSection>
    </>
  );
}

function LogRow({ entry }: { entry: LogEntry }) {
  const levelColor =
    entry.level === "error"
      ? "text-[var(--rago-error)]"
      : entry.level === "warn"
        ? "text-[var(--rago-warn)]"
        : entry.level === "debug" || entry.level === "verbose"
          ? "text-[var(--rago-text-3)]"
          : "text-[var(--rago-text-2)]";
  return (
    <p className="whitespace-pre-wrap break-all">
      <span className="text-[var(--rago-text-3)]">{fmtTime(entry.at)}</span>{" "}
      <span className={`uppercase ${levelColor}`}>{entry.level.padEnd(7)}</span>{" "}
      <span className="text-[var(--rago-text-1)]">{entry.msg}</span>
      {entry.attrs
        ? Object.entries(entry.attrs).map(([k, v]) => (
            <span key={k} className="text-[var(--rago-text-3)]">
              {" "}
              {k}={v}
            </span>
          ))
        : null}
    </p>
  );
}
